/**
 * Thin telemetry hooks wrapping the Statsig React SDK.
 *
 * These provide a stable, type-safe interface so product code never
 * imports Statsig directly.
 */

import { useCallback } from 'react';
import { useStatsigClient, useGateValue } from '@statsig/react-bindings';
import type { EventName, EventPropsMap } from '@danny-tasks/shared/telemetry';

/**
 * Returns `true` when the named feature gate is passing for the current user.
 * Falls back to `false` on any error (SDK not ready, gate missing, etc.).
 *
 * Must be rendered inside a `TelemetryProvider` with a valid `clientKey`.
 */
export function useFeatureGate(gate: string): boolean {
  return useGateValue(gate);
}

/**
 * Returns a type-safe event logging function.
 *
 * Usage:
 * ```ts
 * const track = useTrackEvent();
 * track(Events.PAGE_VIEWED, { path: '/dashboard' });
 * ```
 *
 * No-ops gracefully when the Statsig client has not loaded yet.
 */
export function useTrackEvent() {
  const { logEvent, isLoading } = useStatsigClient();

  return useCallback(
    <E extends EventName>(event: E, props: EventPropsMap[E]) => {
      if (isLoading) return;

      const metadata: Record<string, string> = {};
      for (const [key, value] of Object.entries(props)) {
        if (value !== undefined && value !== null) {
          metadata[key] = String(value);
        }
      }
      logEvent(event, undefined, metadata);
    },
    [logEvent, isLoading],
  );
}
